import { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Edit, Loader2, User, ExternalLink, AlertTriangle } from 'lucide-react';
import { useExpertTip } from '@/hooks/useExpertTip';
import { useUpdateExpertTipStatus } from '@/hooks/useUpdateExpertTipStatus';
import { useActiveTopics } from '@/hooks/useExpertTipTopics';

function getFaviconUrl(url: string): string | null {
  try {
    const domain = new URL(url).hostname;
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
  } catch {
    return null;
  }
}

export default function ExpertTipDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: tipData, isLoading, error } = useExpertTip(id!);
  const updateStatus = useUpdateExpertTipStatus();
  const { data: activeTopics = [] } = useActiveTopics();

  useEffect(() => {
    if (tipData?.data) {
      document.title = `${tipData.data.title} | Admin Dashboard`;
    }
    return () => {
      document.title = 'Admin Dashboard';
    };
  }, [tipData]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-pine-pine" />
      </div>
    );
  }

  if (error || !tipData?.data) {
    return (
      <div className="admin-card p-6">
        <p className="text-red-400">Expert tip not found</p>
        <button
          onClick={() => navigate('/admin/expert-tips')}
          className="mt-4 text-pine-pine"
        >
          Back to Expert Tips
        </button>
      </div>
    );
  }

  const tip = tipData.data;
  const isExpertInactive = tip.expert.status === 'inactive';
  const topicName = tip.topicId
    ? activeTopics.find((t) => t.id === tip.topicId)?.title ?? null
    : null;

  const statusBadgeClass = {
    published: 'bg-white border-[1.5px] border-pine-approved text-pine-approved',
    unpublished: 'bg-white border-[1.5px] border-pine-review text-pine-review',
    archived: 'bg-white border-[1.5px] border-pine-draft text-pine-draft',
  }[tip.status] || 'bg-white border-[1.5px] border-pine-draft text-pine-draft';

  const statusLabel = {
    published: 'Published',
    unpublished: 'Unpublished',
    archived: 'Archived',
  }[tip.status] || tip.status;

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Back + Actions Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/admin/expert-tips')}
          className="flex items-center gap-2 text-pine-ink-soft hover:text-pine-ink"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Expert Tips
        </button>
        <Link
          to={`/admin/expert-tips/${id}/edit`}
          className="admin-button-primary flex items-center gap-2"
        >
          <Edit className="w-4 h-4" />
          Edit Tip
        </Link>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Title */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="admin-card p-6"
          >
            <h1 className="text-pine-display font-display font-semibold text-pine-ink">{tip.title}</h1>
          </motion.div>

          {/* Body */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="admin-card p-6"
          >
            <h2 className="text-pine-heading font-semibold text-pine-ink mb-3">Content</h2>
            <p className="text-pine-body text-pine-ink whitespace-pre-wrap leading-relaxed">{tip.body}</p>
          </motion.div>

          {/* Expert */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="admin-card p-6"
          >
            <h2 className="text-pine-heading font-semibold text-pine-ink mb-4">Expert</h2>

            {isExpertInactive && (
              <div className="flex items-start gap-3 p-3 mb-4 rounded-pine-input bg-amber-500/10 border border-amber-500/20">
                <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-500">
                  This expert profile is inactive. This tip will not be visible to users.
                </p>
              </div>
            )}

            <div className="flex items-center gap-4">
              {tip.expert.avatarUrl ? (
                <img
                  src={tip.expert.avatarUrl}
                  alt={tip.expert.name}
                  className="w-16 h-16 rounded-full object-cover border-2 border-pine-line"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-pine-l1 flex items-center justify-center border-2 border-pine-line">
                  <User className="w-8 h-8 text-pine-ink-soft" />
                </div>
              )}
              <div>
                <p className="font-semibold text-pine-ink">{tip.expert.name}</p>
                <p className="text-sm text-pine-ink-soft mt-0.5">{tip.expert.subtitle}</p>
                <span
                  className={`inline-flex items-center rounded-pine-pill px-2 py-0.5 text-xs font-semibold border mt-1.5 ${
                    tip.expert.status === 'active'
                      ? 'bg-white border-[1.5px] border-pine-approved text-pine-approved'
                      : 'bg-white border-[1.5px] border-pine-draft text-pine-draft'
                  }`}
                >
                  {tip.expert.status === 'active' ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </motion.div>

          {/* Topic */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12 }}
            className="admin-card p-6"
          >
            <h2 className="text-pine-heading font-semibold text-pine-ink mb-3">Topic</h2>
            {topicName ? (
              <p className="text-pine-body text-pine-ink">{topicName}</p>
            ) : (
              <p className="text-pine-body text-pine-ink-soft italic">No topic assigned</p>
            )}
          </motion.div>

          {/* Link Resources */}
          {tip.linkResources.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="admin-card p-6"
            >
              <h2 className="text-pine-heading font-semibold text-pine-ink mb-4">
                Link Resources
                <span className="ml-2 text-sm font-normal text-pine-ink-soft">
                  ({tip.linkResources.length})
                </span>
              </h2>
              <div className="space-y-3">
                {tip.linkResources.map((lr) => {
                  const faviconUrl = getFaviconUrl(lr.url);
                  return (
                    <div
                      key={lr.id}
                      className="flex items-start gap-3 p-3 rounded-pine-input border border-pine-line hover:border-pine-pine transition-colors"
                    >
                      {faviconUrl && (
                        <img
                          src={faviconUrl}
                          alt=""
                          className="w-5 h-5 rounded mt-0.5 flex-shrink-0"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-pine-ink">{lr.title}</p>
                        <a
                          href={lr.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs text-pine-pine hover:underline mt-0.5 truncate"
                        >
                          <ExternalLink className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{lr.url}</span>
                        </a>
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </div>

        {/* Right column - Metadata & Actions */}
        <div className="space-y-6">
          {/* Details card */}
          <div className="admin-card p-6 space-y-4">
            <h2 className="text-pine-heading font-semibold text-pine-ink">Details</h2>
            <div className="space-y-3">
              <div>
                <span className="text-pine-label uppercase tracking-[0.12em] font-bold text-pine-ink-soft">Sort Order</span>
                <p className="text-pine-body text-pine-ink mt-0.5">
                  {tip.sortOrder !== null ? tip.sortOrder : '—'}
                </p>
              </div>
              <div>
                <span className="text-pine-label uppercase tracking-[0.12em] font-bold text-pine-ink-soft">Created</span>
                <p className="text-pine-body text-pine-ink mt-0.5">
                  {new Date(tip.createdAt).toLocaleDateString()}
                </p>
              </div>
              <div>
                <span className="text-pine-label uppercase tracking-[0.12em] font-bold text-pine-ink-soft">Updated</span>
                <p className="text-pine-body text-pine-ink mt-0.5">
                  {new Date(tip.updatedAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>

          {/* Status card */}
          <div className="admin-card p-6 space-y-4">
            <h2 className="text-pine-heading font-semibold text-pine-ink">Status</h2>

            <span className={`inline-flex items-center rounded-pine-pill px-3 py-1 text-sm font-semibold border ${statusBadgeClass}`}>
              {statusLabel}
            </span>

            <div className="space-y-2">
              {tip.status === 'published' && (
                <>
                  <button
                    onClick={() => updateStatus.mutate({ id: id!, status: 'unpublished' })}
                    disabled={updateStatus.isPending}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-pine-input border-[1.5px] border-pine-review text-pine-review hover:bg-pine-hover-button disabled:opacity-50 transition-colors"
                  >
                    Unpublish
                  </button>
                  <button
                    onClick={() => updateStatus.mutate({ id: id!, status: 'archived' })}
                    disabled={updateStatus.isPending}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-pine-input border-[1.5px] border-pine-draft text-pine-draft hover:bg-pine-hover-button disabled:opacity-50 transition-colors"
                  >
                    Archive
                  </button>
                </>
              )}
              {tip.status === 'unpublished' && (
                <>
                  <button
                    onClick={() => updateStatus.mutate({ id: id!, status: 'published' })}
                    disabled={updateStatus.isPending}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-pine-input bg-pine-approved text-white hover:opacity-90 disabled:opacity-50 transition-colors"
                  >
                    Publish
                  </button>
                  <button
                    onClick={() => updateStatus.mutate({ id: id!, status: 'archived' })}
                    disabled={updateStatus.isPending}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-pine-input border-[1.5px] border-pine-draft text-pine-draft hover:bg-pine-hover-button disabled:opacity-50 transition-colors"
                  >
                    Archive
                  </button>
                </>
              )}
              {tip.status === 'archived' && (
                <button
                  onClick={() => updateStatus.mutate({ id: id!, status: 'published' })}
                  disabled={updateStatus.isPending}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-pine-input bg-pine-approved text-white hover:opacity-90 disabled:opacity-50 transition-colors"
                >
                  Publish
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
